import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Eye, 
  Search, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  Lock,
  Zap,
  Monitor
} from 'lucide-react';

const CyberSecurityPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const services = [
    {
      title: "Advanced Persistent Threat (APT) Detection",
      description: "Sophisticated threat hunting and detection systems to identify and neutralize advanced persistent threats targeting your organization.",
      icon: Eye,
      features: [
        "Real-time threat monitoring",
        "Behavioral analysis and anomaly detection",
        "Advanced malware identification",
        "Attribution and threat actor profiling",
        "Incident response coordination"
      ]
    },
    {
      title: "Cyber Intelligence Gathering",
      description: "Strategic intelligence collection and analysis to provide actionable insights on cyber threats and adversaries.",
      icon: Search,
      features: [
        "Dark web monitoring",
        "Threat landscape analysis", 
        "Adversary tracking and profiling",
        "Strategic threat intelligence reports",
        "Predictive threat modeling"
      ]
    },
    {
      title: "Vulnerability Assessment & Penetration Testing",
      description: "Comprehensive security assessments to identify vulnerabilities and validate security controls through ethical hacking.",
      icon: Shield,
      features: [
        "Network and web application testing",
        "Social engineering assessments",
        "Wireless security testing",
        "Physical security evaluations",
        "Compliance validation testing"
      ]
    },
    {
      title: "CDR IPDR Analysis for Government",
      description: "Specialized call detail record and internet protocol detail record analysis for government investigations.",
      icon: Monitor,
      features: [
        "Communication pattern analysis",
        "Location tracking and mapping",
        "Network relationship mapping",
        "Timeline reconstruction",
        "Court-admissible reporting"
      ]
    }
  ];

  const capabilities = [
    {
      title: "Certified Security Experts",
      description: "Team of CISSP, CEH, and GCIH certified professionals",
      icon: Users
    },
    {
      title: "Rapid Response",
      description: "Emergency incident response within 1 hour",
      icon: Zap
    }
  ];

  const governmentClients = [
    "Department of Defense",
    "Department of Homeland Security", 
    "Federal Bureau of Investigation",
    "Central Intelligence Agency",
    "National Security Agency",
    "Various Federal Agencies"
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="section-padding relative overflow-hidden"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/5475750/pexels-photo-5475750.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-near-black/90 z-0" />
        <div className="absolute inset-0 grid-pattern opacity-20 z-1" />
        
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-16 flex items-center justify-center">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="heading-font text-4xl md:text-6xl font-bold text-off-white">
                  Cyber Security & <span className="text-royal-gold">Intelligence</span>
                </h1>
              </div>
            </div>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              Advanced cybersecurity solutions and intelligence gathering for critical infrastructure 
              protection and national security operations. Trusted by government agencies nationwide.
            </p>
                        
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="section-padding bg-gray-dark" ref={ref}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Our <span className="text-royal-gold">Cyber Security</span> Services
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Comprehensive cybersecurity and intelligence solutions designed to protect 
              against sophisticated threats and support national security objectives.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="service-card group"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-12 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <service.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="heading-font text-2xl font-semibold text-off-white group-hover:text-royal-gold transition-colors duration-300">
                    {service.title}
                  </h3>
                </div>
                
                <p className="text-gray-300 mb-6 leading-relaxed">
                  {service.description}
                </p>
                
                <div className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-3">
                      <CheckCircle className="w-4 h-4 text-royal-gold flex-shrink-0" />
                      <span className="text-gray-200 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      {/* <section className="section-padding bg-near-black">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Our <span className="text-royal-gold">Capabilities</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-morphism p-6 rounded-16 text-center hover:border-royal-gold transition-all duration-300 group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-royal-gold to-gold-light rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <capability.icon className="w-8 h-8 text-near-black" />
                </div>
                <h3 className="text-lg font-semibold text-off-white mb-2 group-hover:text-royal-gold transition-colors duration-300">
                  {capability.title}
                </h3>
                <p className="text-gray-300 text-sm">
                  {capability.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}



    </div>
  );
};

export default CyberSecurityPage;